import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import {
  CoinType,
  coinWalletsGQL,
  InventoriesOutput,
  Inventory,
  InventoryAvatarData,
  Query,
} from "@onmo/graphql/graphql";
import {
  applyInventoryGQL,
  purchaseInventoryGQL,
  getListInventoriesGQL,
} from "@onmo/graphql/graphql";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { addPopupQueue, removePopupQueue } from "../../slices/popup";
import { PopupType } from "../../constants/popup";
import { setOpenBundle } from "../../slices/shop";
import { showSnackbar } from "../../slices/alert";
import { useTranslation } from "react-i18next";

const inputPurchaseInventory = (openingInventory: Inventory) => {
  return {
    input: {
      applyImmediately: openingInventory.applyImmediately,
      inventoryId: openingInventory.id,
      inventoryCost: !openingInventory.costs?.length
        ? undefined
        : {
            coinType: openingInventory.costs[0].coinType,
            amount: openingInventory.costs[0].amount,
          },
    },
  };
};

export const usePurchaseInventory = () => {
  const dispatch = useAppDispatch();
  const [purchaseInventoryAction] = useMutation(purchaseInventoryGQL);
  const { openBundle } = useAppSelector((state) => state.shop);

  //purchase avatar/bundle function
  const purchaseInventory = async (
    inventory: Inventory,
    isBuyBundle?: boolean,
  ) => {
    try {
      await purchaseInventoryAction({
        variables: inputPurchaseInventory(inventory),
        update(cache) {
          //update current purchased bundle or avatar
          const inventoriesId = cache.identify({
            __typename: "Inventory",
            id: inventory.id,
          });
          if (inventoriesId) {
            cache.modify({
              id: inventoriesId,
              fields: {
                isPurchased() {
                  return true;
                },
              },
            });
          }
          if (isBuyBundle) {
            //update list purchased avatar in current bundle
            const existingAvatarByBundle = cache.readQuery<{
              inventories: InventoriesOutput;
            }>({
              query: getListInventoriesGQL,
              variables: {
                input: { filters: { bundleInventoryId: inventory.id } },
              },
            });
            if (existingAvatarByBundle?.inventories?.items) {
              existingAvatarByBundle.inventories.items.forEach(
                (item: Inventory) => {
                  const inventoryCacheId = cache.identify({
                    __typename: "Inventory",
                    id: item.id,
                  });
                  if (inventoryCacheId) {
                    cache.modify({
                      id: inventoryCacheId,
                      fields: {
                        isPurchased() {
                          return true;
                        },
                      },
                    });
                  }
                },
              );
            }
          }

          if (inventory?.costs?.length) {
            // Update the coinWalletsGQL query cache directly
            const existingCoinWallets = cache.readQuery<Query>({
              query: coinWalletsGQL,
            });
            if (existingCoinWallets?.me?.coinWallets) {
              const updatedCoinWallets = existingCoinWallets.me.coinWallets.map(
                (wallet) => {
                  if (wallet.itemType === CoinType.SpendableGems) {
                    return {
                      ...wallet,
                      balance: wallet.balance - inventory.costs[0]?.amount,
                    };
                  }
                  return wallet;
                },
              );
              cache.writeQuery<Query>({
                query: coinWalletsGQL,
                data: {
                  ...existingCoinWallets,
                  me: {
                    ...existingCoinWallets.me,
                    coinWallets: updatedCoinWallets,
                  },
                },
              });
            }
          }
        },
      });
      dispatch(removePopupQueue({ type: PopupType.PurchaseInventory }));
      dispatch(addPopupQueue({ type: PopupType.PurchaseResult }));
      if (openBundle && openBundle?.id === inventory.id) {
        dispatch(
          setOpenBundle({ openBundle: { ...openBundle, isPurchased: true } }),
        );
      }
    } catch (error) {
      dispatch(removePopupQueue({ type: PopupType.PurchaseInventory }));
      dispatch(addPopupQueue({ type: PopupType.OutOfGems }));
      console.error(error);
    }
  };

  return purchaseInventory;
};
export const usePurchaseRevive = () => {
  const dispatch = useAppDispatch();
  const [purchaseInventoryAction] = useMutation(purchaseInventoryGQL);

  const purchaseRevive = async (inventory: Inventory) => {
    if (!inventory) throw new Error("Inventory not found");
    try {
      await purchaseInventoryAction({
        variables: {
          // Hardcoded inventory
          input: {
            inventoryId: inventory.id,
            inventoryCost: {
              coinType: inventory.costs[0].coinType,
              amount: inventory.costs[0].amount,
            },
          },
        },
      });
      dispatch(removePopupQueue({ type: PopupType.GameLose }));
    } catch (error) {
      dispatch(addPopupQueue({ type: PopupType.OutOfGems }));
      console.error(error);
    }
  };

  return purchaseRevive;
};

// equip avatar function
export const useApplyInventory = (openingInventory?: Inventory) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [applyInventoryAction] = useMutation(applyInventoryGQL);

  const applyInventory = useCallback(() => {
    applyInventoryAction({
      variables: { input: { inventoryId: openingInventory?.id } },
      update(cache) {
        cache.modify({
          id: "ROOT_QUERY",
          fields: {
            me(existingMe) {
              const userId = existingMe.__ref;
              cache.modify({
                id: userId,
                fields: {
                  avatar() {
                    dispatch(
                      showSnackbar({
                        message: t("profile_Profile update successfully!"),
                      }),
                    );
                    return (openingInventory?.data as InventoryAvatarData).url;
                  },
                },
              });
              return existingMe;
            },
          },
        });
      },
    });
  }, [applyInventoryAction, openingInventory, dispatch, t]);

  return applyInventory;
};
